import { useState, useRef, useEffect, useCallback, type KeyboardEvent, type ClipboardEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { verifyEmail, resendVerification } from '../api/auth';
import Footer from '../components/Footer';
import FloatingLanguageSwitcher from '../components/FloatingLanguageSwitcher';
import { Card } from '../components/ui';

const CODE_LENGTH = 6;
const RESEND_COOLDOWN = 60;

export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useAuthStore((s) => s.setAuth);

  const email = (location.state as { email?: string } | null)?.email ?? '';

  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMessage, setResendMessage] = useState('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Redirect to register if no email was provided
  useEffect(() => {
    if (!email) {
      navigate('/register', { replace: true });
    }
  }, [email, navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const submitCode = useCallback(async (code: string) => {
    setError('');
    setLoading(true);
    try {
      const data = await verifyEmail(email, code);
      // Access token stored in memory via setAuth (not localStorage)
      setAuth(data.access_token, data.user);
      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr.response?.data?.detail ?? t('auth.verify.verifyError'));
      // Clear digits on error so user can retry
      setDigits(Array(CODE_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  }, [email, navigate, setAuth, t]);

  const handleChange = (index: number, value: string) => {
    // Only accept single digits
    const digit = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);

    if (digit && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are filled
    if (digit && newDigits.every((d) => d !== '')) {
      void submitCode(newDigits.join(''));
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;

    const newDigits = Array(CODE_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) {
      newDigits[i] = pasted[i];
    }
    setDigits(newDigits);

    // Focus the next empty input or the last one
    const nextEmpty = newDigits.findIndex((d) => d === '');
    inputRefs.current[nextEmpty >= 0 ? nextEmpty : CODE_LENGTH - 1]?.focus();

    // Auto-submit if all filled
    if (newDigits.every((d) => d !== '')) {
      void submitCode(newDigits.join(''));
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setResendMessage('');
    try {
      const data = await resendVerification(email);
      setResendMessage(data.message ?? t('auth.verify.resendSuccess'));
      setResendCooldown(RESEND_COOLDOWN);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr.response?.data?.detail ?? t('auth.verify.resendError'));
    }
  };

  if (!email) return null;

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 dark:bg-slate-950">
      <FloatingLanguageSwitcher />
      <main id="main-content" tabIndex={-1} className="flex flex-1 items-center justify-center px-4 outline-none">
        <div className="w-full max-w-md">
          <Card className="space-y-6 p-8">
            {/* Brand */}
            <div className="text-center">
              <svg className="mx-auto h-12 w-12" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                <defs>
                  <linearGradient id="logoGrad" x1="4" y1="8" x2="44" y2="40" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#3B82F6"/>
                    <stop offset="100%" stopColor="#4F46E5"/>
                  </linearGradient>
                </defs>
                <path d="M13.5 38C7.701 38 3 33.299 3 27.5c0-4.58 2.942-8.468 7.038-9.87C10.98 11.583 16.046 7 22 7c6.904 0 12.5 5.596 12.5 12.5 0 .463-.025.92-.074 1.37C38.014 22.16 41 25.616 41 29.75 41 34.86 36.86 39 31.75 39H13.5z" fill="url(#logoGrad)"/>
                <path d="M13.5 38C7.701 38 3 33.299 3 27.5c0-4.58 2.942-8.468 7.038-9.87C10.98 11.583 16.046 7 22 7c6.904 0 12.5 5.596 12.5 12.5 0 .463-.025.92-.074 1.37C38.014 22.16 41 25.616 41 29.75 41 34.86 36.86 39 31.75 39H13.5z" fill="white" opacity="0.15" mask="url(#logoShine)"/>
                <mask id="logoShine"><rect x="0" y="0" width="48" height="24" fill="white"/></mask>
              </svg>
              <h1 className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
                {t('auth.verify.title')}
              </h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {t('auth.verify.subtitlePrefix')} <span className="font-medium text-slate-700 dark:text-slate-300">{email}</span>
              </p>
            </div>

            {/* Error */}
            {error && (
              <div
                className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-400"
                role="alert"
              >
                {error}
              </div>
            )}

            {/* Resend success message */}
            {resendMessage && (
              <div
                className="rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-700 dark:border-green-500/20 dark:bg-green-500/10 dark:text-green-400"
                role="status"
              >
                {resendMessage}
              </div>
            )}

            {/* Code inputs */}
            <div className="flex justify-center gap-3" role="group" aria-label={t('auth.verify.codeGroupLabel')}>
              {digits.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => { inputRefs.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  onPaste={i === 0 ? handlePaste : undefined}
                  disabled={loading}
                  autoFocus={i === 0}
                  aria-label={t('auth.verify.digitLabel', { position: i + 1 })}
                  className="h-14 w-12 rounded-xl border border-slate-200 bg-white text-center text-xl font-semibold text-slate-900 transition-colors focus:border-blue-500 focus:outline-none focus:ring-[3px] focus:ring-blue-500/20 disabled:opacity-50 dark:border-white/10 dark:bg-slate-900/80 dark:text-white"
                />
              ))}
            </div>

            {/* Loading indicator */}
            {loading && (
              <div className="flex items-center justify-center gap-2 text-sm text-slate-500 dark:text-slate-400" role="status">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent dark:border-blue-400 dark:border-t-transparent" />
                <span>{t('auth.verify.verifying')}</span>
              </div>
            )}

            {/* Resend code */}
            <div className="text-center text-sm text-slate-500 dark:text-slate-400">
              {t('auth.verify.didntReceive')}{' '}
              <button
                type="button"
                onClick={handleResend}
                disabled={resendCooldown > 0 || loading}
                className="font-medium text-blue-600 hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50 dark:text-blue-400"
              >
                {resendCooldown > 0
                  ? t('auth.verify.resendCooldown', { seconds: resendCooldown })
                  : t('auth.verify.resend')}
              </button>
            </div>

            <p className="text-center text-sm text-slate-500 dark:text-slate-400">
              <Link to="/register" className="font-medium text-blue-600 hover:underline dark:text-blue-400">
                {t('auth.verify.backToRegister')}
              </Link>
            </p>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}
